import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clapperboard, Image, Library, Lightbulb, Sparkles, Video, Wand2 } from "lucide-react";

const actions = [
  { to: "/luo-promptti", label: "Luo prompti", icon: Wand2, description: "Täytä helppo lomake ja saat valmiin promptin." },
  { to: "/ideasta-promptiksi", label: "Ideasta promptiksi", icon: Lightbulb, description: "Kirjoita oma idea ja muuta se työkalukohtaiseksi promptiksi." },
  { to: "/lyhytvideo", label: "Lyhytvideon käsikirjoitus", icon: Clapperboard, description: "Tee kuvauskelpoinen Reels-, TikTok- tai Shorts-prompti." },
  { to: "/paranna", label: "Paranna promptia", icon: Sparkles, description: "Tee omasta promptistasi selkeämpi." },
  { to: "/pohjat", label: "Promptipohjat", icon: BookOpen, description: "Valitse valmis pohja ja kopioi prompti käyttöösi." },
  { to: "/pohjat/ai-kuvapromptit-master", label: "AI-kuvaprompti", icon: Image, description: "Rakenna realistinen kuvaprompti valmiilla visuaalisilla valinnoilla." },
  { to: "/pohjat/ai-videopromptit-master", label: "AI-videoprompti", icon: Video, description: "Ohjaa liike, kamera ja jatkuvuus valmiilla valinnoilla." },
  { to: "/kirjasto", label: "Promptikirjasto", icon: Library, description: "Löydä tallentamasi promptit myöhemmin." },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden" data-testid="home-hero-section">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1667827576128-374c6ab46ee2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
            alt=""
            className="h-full w-full object-cover object-center opacity-18"
            data-testid="home-hero-background-image"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FDFCF8]/70 via-[#FDFCF8]/92 to-[#FDFCF8]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pb-12 pt-16 sm:px-6 sm:pb-16 sm:pt-24 lg:px-8">
          <div className="max-w-4xl animate-fade-up" data-testid="home-hero-content">
            <p className="mb-5 inline-flex rounded-full bg-white/80 px-4 py-2 text-sm font-extrabold text-[#4A6753] ring-1 ring-[#E8E5D9]" data-testid="home-hero-kicker">
              Käytännön promptivalmentaja sisällöntuotantoon
            </p>
            <h1 className="font-heading text-4xl font-bold leading-tight text-[#2C2F2E] sm:text-5xl lg:text-6xl" data-testid="home-hero-title">
              Luo parempia AI-prompteja nopeammin
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-[#5C6360] md:text-lg" data-testid="home-hero-description">
              FinnCreators Prompti Studio auttaa sinua rakentamaan selkeitä prompteja someen, kuviin, videoihin ja markkinointiin.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="home-hero-actions">
              <Link to="/luo-promptti" className="inline-flex items-center gap-2 rounded-full bg-[#4A6753] px-6 py-4 text-sm font-bold text-white shadow-[0_10px_28px_rgb(74,103,83,0.22)] transition-transform duration-200 hover:-translate-y-0.5" data-testid="home-create-prompt-button">
                Luo prompti <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link to="/ideasta-promptiksi" className="inline-flex items-center gap-2 rounded-full bg-[#D97757] px-6 py-4 text-sm font-bold text-white shadow-[0_10px_28px_rgb(217,119,87,0.2)] transition-transform duration-200 hover:-translate-y-0.5" data-testid="home-idea-prompt-button">
                Muuta ideani promptiksi <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link to="/rakenneopas" className="inline-flex items-center gap-2 rounded-full bg-white/80 px-6 py-4 text-sm font-bold text-[#2C2F2E] ring-1 ring-[#E8E5D9] transition-transform duration-200 hover:-translate-y-0.5" data-testid="home-guide-button">
                Katso hyvä rakenne
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8" data-testid="home-action-grid-section">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="home-action-grid">
          {actions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.to}
                to={action.to}
                className="group rounded-[1.75rem] border border-[#E8E5D9] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_42px_rgb(0,0,0,0.08)]"
                data-testid={`home-action-card-${index}`}
              >
                <span className="mb-6 grid h-12 w-12 place-items-center rounded-2xl bg-[#F5F3EC] text-[#4A6753] transition-colors duration-300 group-hover:bg-[#4A6753] group-hover:text-white" data-testid={`home-action-icon-${index}`}>
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h2 className="font-heading text-xl font-bold text-[#2C2F2E]" data-testid={`home-action-title-${index}`}>{action.label}</h2>
                <p className="mt-2 text-sm leading-6 text-[#5C6360]" data-testid={`home-action-description-${index}`}>{action.description}</p>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}